import cookies from './cookies'
import toMinorUnits from '../tools/minorUnits'

// UA
// this is what we have since release of new shop
// https://developers.google.com/analytics/devguides/collection/ua/gtm/enhanced-ecommerce#product-impressions
// https://developers.google.com/analytics/devguides/collection/gtagjs/enhanced-ecommerce#impression-data

// GA4
// https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm
// https://support.google.com/analytics/answer/9267735?hl=en&ref_topic=9756175

// FB
// https://developers.facebook.com/docs/meta-pixel/reference?checkpoint_src=1501092823525282#events
// https://drive.google.com/file/d/1maKxwrkm-rRpjrBH-MadB_Che9xVbgMZ/view

// The EEA, UK and Switzerland require opt-in consent before anything
// non-essential runs. The rest of the world - including every US state privacy
// law in force - works on an opt-out model instead, so measurement runs by
// default there and stops when a visitor opts out.
//
// Google resolves which default applies from the visitor's own location. This
// deliberately does not key off our region config or the hostname: accu.co.uk
// is the "Worldwide" region and serves plenty of EEA traffic, and European
// visitors reach accu-components.com too.
const OPT_IN_REGIONS = [
    'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR',
    'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK',
    'SI', 'ES', 'SE', 'IS', 'LI', 'NO', 'GB', 'CH',
    // Google can report these places under their own codes rather than
    // their country's, so the country codes above don't catch them: the
    // EU's outermost regions (Reunion, Guadeloupe, Martinique, French Guiana,
    // Mayotte, Saint-Martin) and Aland are in the EU, and Gibraltar, Jersey,
    // Guernsey and the Isle of Man have their own GDPR-based laws.
    'RE', 'GP', 'MQ', 'GF', 'YT', 'MF', 'AX', 'GI', 'JE', 'GG', 'IM',
]

// Global Privacy Control is a browser-level opt-out signal. Twelve US states
// now require it to be honoured, so it suppresses the advertising signals
// wherever it is set, and an "accept all" click does not override it.
function hasGlobalPrivacyControl() {
    return window.navigator?.globalPrivacyControl === true
}

function sendClarityConsent(clarityConsent) {
    window.clarity = window.clarity || function () {
        (window.clarity.q  = window.clarity.q || []).push(arguments)
    }
    window.clarity('consentv2', clarityConsent)
}

const gtm = (data) => ({
    ...cookies(),
    tag_manager_id: data.tag_manager_id,
    ga_measurement_id: data.ga_measurement_id,
    enabled: false,
    currentConsent: null,
    init() {
        this.enable()
    },
    enable() {
        if (this.enabled) {
            this.updateConsent()
            return
        }

        const gtmSetupData = {
            'gtm.start': new Date().getTime(),
            event: 'gtm.js',
            send_page_view: false,
        }

        if (window.Alpine.store('user').isLoggedIn) {
            // if user is logged in lets send their customer ID
            gtmSetupData.gcp_id_customer = window.Alpine.store('user').id_customer

            const subscriptions = window.Alpine.store('user').accupro_subscriptions
            const activeSubscriptions = Object.values(subscriptions ?? {}).filter(subscription => subscription.end_date !== null && subscription.end_date !== '0000-00-00 00:00:00' && new Date(subscription.end_date) > new Date())

            if (activeSubscriptions.length > 0) {
                gtmSetupData.gcp_accupro_subscription = 'yes'
            } else {
                gtmSetupData.gcp_accupro_subscription = 'no'
            }
        }

        if (data.content_id) {
            gtmSetupData.content_id = data.content_id
        }

        if (data.content_group) {
            gtmSetupData.content_group = data.content_group
        }

        window.dataLayer = window.dataLayer || []
        window.uetq = window.uetq || []

        // Opt-in regions: nothing non-essential until the visitor answers the
        // banner. wait_for_update holds Google's tags briefly so a visitor who
        // does consent is not measured in denied mode while their answer lands.
        this.gtag('consent', 'default', {
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied',
            analytics_storage: 'denied',
            wait_for_update: 500,
            region: OPT_IN_REGIONS,
        })

        // Everywhere else, unless the browser is sending Global Privacy Control.
        const optOutDefault = hasGlobalPrivacyControl() ? 'denied' : 'granted'
        this.gtag('consent', 'default', {
            ad_storage: optOutDefault,
            ad_user_data: optOutDefault,
            ad_personalization: optOutDefault,
            analytics_storage: 'granted',
        })

        // Microsoft UET has no region parameter, so its default here is denied
        // everywhere. The region logic reaches UET through GTM instead: the base
        // UET tag ("Bing") has "Inherit initial consent from GTM" ticked, which
        // copies Google's resolved state - region defaults included - into uetq
        // when the tag starts. This default is the fail-safe if GTM never loads.
        window.uetq.push('consent', 'default', {
            ad_storage: 'denied',
        })

        this.updateConsent()

        this.gtag('set', 'url_passthrough', true);

        ((w, d, s, l, i) => {
            w[l] = w[l] || []
            w[l].push(gtmSetupData)
            const f = d.getElementsByTagName(s)[0]
            const j = d.createElement(s)
            const dl = l !== 'dataLayer' ? `&l=${l}` : ''
            j.async = true
            j.src = `https://www.googletagmanager.com/gtm.js?id=${i}${dl}`
            f.parentNode.insertBefore(j, f)
        })(window, document, 'script', 'dataLayer', this.tag_manager_id)

        this.enabled = true

        // Prevent url_passthrough parameters being added to Sirv links
        document.addEventListener('click', (e) => {
            const link = e.target.closest('a[href]')
            const params = ['gclid', 'dclid', 'gclsrc', '_gl', 'wbraid', 'gbraid']
            if (link?.href?.includes(process.env.MIX_CDN_IMAGE)) {
                const cleanUrl = new URL(link.href)
                params.forEach((param) => cleanUrl.searchParams.delete(param))
                link.href = cleanUrl.toString()
            }
        })
    },
    gtag() {
        window.dataLayer.push(arguments)
    },
    updateConsent() {
        if (!window.Alpine.store('cookies')?.consent) {
            return
        }

        // A browser-level opt-out outranks a stored banner choice.
        const adStorage = (!hasGlobalPrivacyControl() && this.getCookieOption('adStorage'))
            ? 'granted'
            : 'denied'

        const consent = {
            analytics_storage: this.getCookieOption('analytics') ? 'granted' : 'denied',
            ad_storage: adStorage,
            ad_user_data: adStorage,
            ad_personalization: adStorage,
        }

        if (JSON.stringify(consent) === JSON.stringify(this.currentConsent)) {
            return
        }
        this.currentConsent = consent

        this.gtag('consent', 'update', consent)
        window.uetq.push('consent', 'update', {
            ad_storage: adStorage,
        })
        sendClarityConsent({
            ad_storage: adStorage,
            analytics_storage: this.getCookieOption('analytics') ? 'granted' : 'denied',
        })
    },
    pushToDataLayer(event) {
        // dont push to data layer when disabled analytics
        // at this point user could disable but component was already initiated
        this.sendToTagManager(event)
        window.accuDataLayer.push(event.detail)
    },
    sendToTagManager(event) {
        window.dataLayer.push(event.detail)
    },
    roundUp(value) {
        return parseFloat((Math.round(value * 100) / 100).toFixed(2))
    },
    productClick(event) {
        // UA
        this.sendToTagManager(event)
        // GA4 - change data from UA format
        const ga4products = []
        event.detail.ecommerce.click.products.forEach((product) => {
            ga4products.push({
                item_list_name: event.detail.ecommerce.click.actionField.list,
                item_list_id: event.detail.ecommerce.click.actionField.list.replace(/[^0-9a-z]/gi, '_').toLowerCase(),
                item_id: product.reference,
                item_name: product.name,
                item_category: product.category,
                index: product.position - 1,
            })
        })

        const ga4Object = {
            detail: {
                event: 'select_item',
                ecommerce: {
                    // not mentioned in docs , but otherwise what is the point of price
                    currency: window.Alpine.store('currency').iso_code,
                    item_list_name: event.detail.ecommerce.click.actionField.list,
                    item_list_id: event.detail.ecommerce.click.actionField.list.replace(/[^0-9a-z]/gi, '_').toLowerCase(),
                    items: ga4products,
                },
            },
        }
        this.pushToDataLayer({
            detail: { ecommerce: null },
        })

        this.sendToTagManager(ga4Object)

        this.pushToDataLayer({
            detail: { ecommerce: null },
        })
    },
    productUpdateEvent(event, product) {
        // A line sent without the product's details (removing discontinued
        // products does this) has nothing to report
        if (!product?.productDetails) {
            return
        }

        // 'Empty cart' removes every line in one event, so each line carries
        // its own quantity change
        const quantityChange = product.quantityChange ?? event.detail.quantityChange

        const gtmObject = {
            event: null,
            ecommerce: {
                currencyCode: window.Alpine.store('currency').iso_code,
            },
        }

        let ga4Object = null

        const gtmProduct = {
            name: product.productDetails.name,
            id: product.productDetails.reference,
            price: product.productDetails.priceUnitExTax,
            quantity: parseInt(product.quantity, 10),
            list: event.detail.fromPage,
        }

        if (quantityChange) {
            // return true // if we change our minds about keeping track of changes that happen in cart
            gtmProduct.quantity = parseInt(quantityChange, 10)
        }

        if (event.detail.cartAction === 'remove') {
            // removing from cart
            gtmObject.event = 'removeFromCart'
            gtmObject.ecommerce.remove = {
                products: [
                    gtmProduct,
                ],
            }

            ga4Object = {
                event: 'remove_from_cart',
                ecommerce: {
                    // GA4 reads `currency`. `currencyCode` is the Universal
                    // Analytics spelling and is kept because the Reddit Pixel
                    // tags in GTM still read ecommerce.currencyCode.
                    currency: window.Alpine.store('currency').iso_code,
                    currencyCode: window.Alpine.store('currency').iso_code,
                    value: this.roundUp(product.productDetails.priceUnitExTax * (parseInt(quantityChange, 10) ? quantityChange : parseInt(product.quantity, 10))),
                    items: [
                        {
                            item_id: product.productDetails.reference,
                            item_name: product.productDetails.name,
                            index: product.productDetails.position,
                            item_list_name: event.detail.fromPage,
                            price: product.productDetails.priceUnitExTax,
                            quantity: quantityChange ? parseInt(quantityChange, 10) : parseInt(product.quantity, 10),
                        },
                    ],
                },
            }
        } else if (event.detail.cartAction === 'add') {
            // adding to cart
            gtmObject.event = 'addToCart'
            gtmObject.ecommerce.add = {
                actionField: {
                    list: event.detail.fromPage,
                },
                products: [
                    gtmProduct,
                ],
            }

            ga4Object = {
                event: 'add_to_cart',
                ecommerce: {
                    currency: window.Alpine.store('currency').iso_code,
                    currencyCode: window.Alpine.store('currency').iso_code,
                    value: this.roundUp(product.productDetails.priceUnitExTax * (quantityChange ? parseInt(quantityChange, 10) : parseInt(product.quantity, 10))),
                    items: [
                        {
                            item_id: product.productDetails.reference,
                            item_name: product.productDetails.name,
                            index: product.productDetails.position,
                            item_list_name: event.detail.fromPage,
                            price: product.productDetails.priceUnitExTax,
                            quantity: quantityChange ? parseInt(quantityChange, 10) : parseInt(product.quantity, 10),
                        },
                    ],
                },
            }

            // Whole pence or cents, for ad pixels that reject decimals (OpenAI)
            ga4Object.ecommerce.value_minor = toMinorUnits(ga4Object.ecommerce.value)
            ga4Object.ecommerce.items[0].price_minor = toMinorUnits(product.productDetails.priceUnitExTax)

            // FB/meta
            this.pushToDataLayer({
                detail: {
                    event: 'AddToCart',
                    fb_data: {
                        content_ids: [product.productDetails.reference],
                        content_name: product.productDetails.name,
                        content_type: 'product',
                        content_category: event.detail.fromPage,
                        contents: [
                            {
                                id: product.productDetails.reference,
                                name: product.productDetails.name,
                                price: product.productDetails.priceUnitExTax,
                                quantity: quantityChange ? parseInt(quantityChange, 10) : parseInt(product.quantity, 10),
                            },
                        ],
                        currency: window.Alpine.store('currency').iso_code,
                        value: product.productDetails.priceUnitExTax * (quantityChange ? parseInt(quantityChange, 10) : parseInt(product.quantity, 10)),
                        num_items: quantityChange ? parseInt(quantityChange, 10) : parseInt(product.quantity, 10),
                    },
                },
            })
        }

        this.pushToDataLayer({
            detail: gtmObject,
        })

        this.pushToDataLayer({
            detail: { ecommerce: null },
        })

        this.pushToDataLayer({
            detail: ga4Object,
        })

        this.pushToDataLayer({
            detail: { ecommerce: null },
        })
    },
    logCheckoutStepsProgress(event) {
        const {
            step, options, products, carrier,
        } = event.detail

        // UA
        const gtmObject = {
            event: 'checkout',
            ecommerce: {
                currencyCode: window.Alpine.store('currency').iso_code,
                checkout: {
                    actionField: {
                        step,
                        action: 'checkout',
                    },
                    products: [],
                },
            },
        }

        // GA4
        const ga4Object = {
            event: 'ga4_checkout',
            ecommerce: {
                currency: window.Alpine.store('currency').iso_code,
                currencyCode: window.Alpine.store('currency').iso_code,
                value: 0,
                step,
                items: [],
            },
        }

        // FB
        const fbObject = {
            event: 'InitiateCheckout',
            fb_data: {
                currency: window.Alpine.store('currency').iso_code,
                value: 0,
                num_items: 0,
                content_ids: [],
                content_type: 'product',
                contents: [],
            },
        }

        if ('action' in options && options.action === 'carrier_selection') {
            gtmObject.ecommerce.checkout.actionField.option = carrier.name
        }

        products.forEach((product) => {
            gtmObject.ecommerce.checkout.products.push({
                name: product.name,
                id: product.reference,
                price: product.price,
                quantity: parseInt(product.quantity, 10),
            })

            ga4Object.ecommerce.items.push({
                item_name: product.name,
                item_id: product.reference,
                price: product.price,
                quantity: parseInt(product.quantity, 10),
            })

            fbObject.fb_data.contents.push({
                id: product.reference,
                name: product.name, // replace with contents name
                price: product.price, // replace with contents price
                quantity: parseInt(product.quantity, 10), // replace with item quantity
            })

            ga4Object.ecommerce.value += parseInt(product.quantity, 10) * product.price
            ga4Object.ecommerce.value = this.roundUp(ga4Object.ecommerce.value)

            fbObject.fb_data.value += parseInt(product.quantity, 10) * product.price
            fbObject.fb_data.content_ids.push(product.reference)
            fbObject.fb_data.num_items += parseInt(product.quantity, 10)
        })

        this.pushToDataLayer({
            detail: gtmObject,
        })

        this.pushToDataLayer({
            detail: { ecommerce: null },
        })

        this.pushToDataLayer({
            detail: ga4Object,
        })

        this.pushToDataLayer({
            detail: { ecommerce: null },
        })

        // FB/meta
        if (step === 1) {
            this.pushToDataLayer({
                detail: fbObject,
            })
        }
    },
})
document.addEventListener('alpine:init', () => {
    window.Alpine.data('gtm', gtm)
})
export default gtm
